import { PaginatedResponse, robotoHeaders } from "@/shared/http";
import { HttpClient } from "@/shared/http/HttpClient";
import { parseTimestampsAsBigInt } from "@/shared/json";
import { AuthZTuple, SearchQueryBody } from "@/types";

import {
  CollectionRecord,
  CollectionResourceType,
  UpdateCollectionRequest,
} from "./CollectionRecord";

interface CollectionAccessResponse {
  relations: AuthZTuple[];
}

interface Options {
  abortSignal?: AbortSignal;
  resourceOwnerId?: string;
}

export class CollectionService {
  #httpClient: HttpClient;

  constructor(httpClient: HttpClient) {
    this.#httpClient = httpClient;
  }

  public async getCollectionById(
    collectionId: string,
    options?: Options,
  ): Promise<CollectionRecord> {
    const url = this.#httpClient.constructUrl(
      `v1/collections/id/${collectionId}`,
      new URLSearchParams({ content_mode: "full" }),
    );
    const response = await this.#httpClient.get(url, {
      signal: options?.abortSignal,
    });
    return response.json<CollectionRecord>(parseTimestampsAsBigInt);
  }

  public async updateCollection(
    collectionId: string,
    updates: UpdateCollectionRequest,
    options?: Options,
  ): Promise<CollectionRecord> {
    const url = this.#httpClient.constructUrl(
      `v1/collections/id/${collectionId}`,
    );
    const response = await this.#httpClient.put(url, {
      idempotent: true,
      headers: options?.resourceOwnerId
        ? robotoHeaders({ resourceOwnerId: options.resourceOwnerId })
        : undefined,
      body: JSON.stringify(updates),
      signal: options?.abortSignal,
    });
    return response.json<CollectionRecord>(parseTimestampsAsBigInt);
  }

  public async deleteCollection(
    collectionId: string,
    options?: Options,
  ): Promise<void> {
    const url = this.#httpClient.constructUrl(
      `v1/collections/id/${collectionId}`,
    );
    const response = await this.#httpClient.delete(url, {
      idempotent: false,
      headers: options?.resourceOwnerId
        ? robotoHeaders({ resourceOwnerId: options.resourceOwnerId })
        : undefined,
      signal: options?.abortSignal,
    });
    await response.throwIfError();
  }

  /**
   * Org-scoped list of collections, newest first. Backs collection
   * pickers (e.g. the agent-launch ``COLLECTION_ID`` variable input).
   * ``content_mode=summary_only`` keeps the payload light — callers only
   * need id / name / audit fields, not the full resource set. When
   * ``resourceType`` is set the result is filtered to collections holding
   * that resource type server-side (e.g. ``event`` collections only).
   */
  public async searchCollections(options: {
    orgId: string;
    resourceType?: CollectionResourceType;
    limit?: number;
    abortSignal?: AbortSignal;
  }): Promise<PaginatedResponse<CollectionRecord>> {
    const url = this.#httpClient.constructUrl(
      "v1/collections/search",
      new URLSearchParams({ content_mode: "summary_only" }),
    );
    const body: SearchQueryBody = {
      sort_by: "created",
      sort_direction: "DESC",
      limit: options.limit ?? 200,
    };
    if (options.resourceType !== undefined) {
      body.condition = {
        field: "resource_type",
        comparator: "EQUALS",
        value: options.resourceType,
      };
    }
    const response = await this.#httpClient.post(url, {
      idempotent: true,
      headers: robotoHeaders({ resourceOwnerId: options.orgId }),
      body: JSON.stringify(body),
      signal: options.abortSignal,
    });
    return response.json<PaginatedResponse<CollectionRecord>>();
  }

  public async getCollectionAccess(
    collectionId: string,
    options?: Options,
  ): Promise<CollectionAccessResponse> {
    const url = this.#httpClient.constructUrl(
      `v1/collections/${collectionId}/access`,
    );
    const response = await this.#httpClient.get(url, {
      signal: options?.abortSignal,
      headers: options?.resourceOwnerId
        ? robotoHeaders({ resourceOwnerId: options.resourceOwnerId })
        : undefined,
    });
    return response.json<CollectionAccessResponse>();
  }
}
